import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { supabaseHelpers } from '../lib/supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLock, FiEye, FiEyeOff, FiSave, FiX, FiLoader } = FiIcons;

const PasswordChangeForm = ({ currentUser, onClose, onSuccess }) => {
  const { isDark } = useTheme();
  const [formData, setFormData] = React.useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = React.useState({
    current: false,
    new: false,
    confirm: false
  });
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate form
    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      setError('Tutti i campi sono obbligatori');
      return;
    }

    if (formData.newPassword.length < 6) {
      setError('La nuova password deve essere di almeno 6 caratteri');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Le nuove password non coincidono');
      return;
    }

    if (formData.currentPassword === formData.newPassword) {
      setError('La nuova password deve essere diversa da quella attuale');
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabaseHelpers.changePassword(
        currentUser.id,
        formData.currentPassword,
        formData.newPassword
      );

      if (error) {
        setError(error.message);
      } else {
        onSuccess?.();
        onClose();
      }
    } catch (err) {
      setError('Errore durante il cambio password');
    } finally {
      setLoading(false);
    }
  };

  const toggleShowPassword = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl max-w-md w-full shadow-2xl`}>
        {/* Header */}
        <div className={`${isDark ? 'bg-gradient-to-r from-blue-800 to-blue-900' : 'bg-gradient-to-r from-blue-600 to-blue-700'} text-white p-6 rounded-t-2xl`}>
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold mb-1">Cambia Password</h2>
              <p className="text-blue-100 text-sm">Aggiorna la tua password di accesso</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
              disabled={loading}
            >
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Current Password */}
          <div>
            <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Password Attuale *
            </label>
            <div className="relative">
              <SafeIcon 
                icon={FiLock} 
                className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} 
              />
              <input
                type={showPasswords.current ? 'text' : 'password'}
                value={formData.currentPassword}
                onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                className={`w-full pl-10 pr-12 py-3 rounded-lg border-2 transition-colors focus:outline-none ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                    : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
                }`}
                placeholder="Inserisci password attuale"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => toggleShowPassword('current')}
                className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                disabled={loading}
              >
                <SafeIcon icon={showPasswords.current ? FiEyeOff : FiEye} className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* New Password */}
          <div>
            <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Nuova Password *
            </label>
            <div className="relative">
              <SafeIcon 
                icon={FiLock} 
                className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} 
              />
              <input
                type={showPasswords.new ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                className={`w-full pl-10 pr-12 py-3 rounded-lg border-2 transition-colors focus:outline-none ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                    : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
                }`}
                placeholder="Inserisci nuova password (min 6 caratteri)"
                required
                disabled={loading}
                minLength={6}
              />
              <button
                type="button"
                onClick={() => toggleShowPassword('new')}
                className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                disabled={loading}
              >
                <SafeIcon icon={showPasswords.new ? FiEyeOff : FiEye} className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Conferma Nuova Password *
            </label>
            <div className="relative">
              <SafeIcon 
                icon={FiLock} 
                className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} 
              />
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className={`w-full pl-10 pr-12 py-3 rounded-lg border-2 transition-colors focus:outline-none ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                    : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
                }`}
                placeholder="Conferma nuova password"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => toggleShowPassword('confirm')}
                className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                disabled={loading}
              >
                <SafeIcon icon={showPasswords.confirm ? FiEyeOff : FiEye} className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Password Requirements */}
          <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
            <h4 className={`text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Requisiti Password:
            </h4>
            <ul className={`text-xs space-y-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              <li className={formData.newPassword.length >= 6 ? 'text-green-600' : ''}>
                • Almeno 6 caratteri {formData.newPassword.length >= 6 && '✓'}
              </li>
              <li className={formData.newPassword && formData.confirmPassword && formData.newPassword === formData.confirmPassword ? 'text-green-600' : ''}>
                • Le password devono coincidere {formData.newPassword && formData.confirmPassword && formData.newPassword === formData.confirmPassword && '✓'}
              </li>
              <li className={formData.currentPassword && formData.newPassword && formData.currentPassword !== formData.newPassword ? 'text-green-600' : ''}>
                • Diversa dalla password attuale {formData.currentPassword && formData.newPassword && formData.currentPassword !== formData.newPassword && '✓'}
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex space-x-4 pt-2">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-4 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? (
                <>
                  <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
                  <span>Aggiornando...</span>
                </>
              ) : (
                <>
                  <SafeIcon icon={FiSave} className="w-5 h-5" />
                  <span>Aggiorna Password</span>
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-colors ${
                isDark 
                  ? 'bg-gray-600 text-white hover:bg-gray-500' 
                  : 'bg-gray-500 text-white hover:bg-gray-600'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              Annulla
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PasswordChangeForm;